/* eslint-disable no-lonely-if */
import React, { useEffect, useState } from "react";
import {
  Routes, Route, useParams,
} from "react-router-dom";
import LoginPage from "../pages/LoginPage";
import NotFoundPage from "../pages/NotFoundPage";
import { isSessionCookieTrue, getVanityFromCookie } from "../../utils/authCookies";

const setFavicon = (href) => {
  if (!href) return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
};

const normalizeVanity = (value) => {
  const vanity = (value || '').trim().toLowerCase();
  return /^[a-z0-9-]+$/.test(vanity) ? vanity : '';
};

const readLoginHint = () => {
  if (typeof window === 'undefined' || !window.location) return '';
  const value = new URLSearchParams(window.location.search).get('login_hint') || '';
  // Light validation — reject anything that doesn't smell like an email so we
  // never forward stray query strings into Entra's login_hint param.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? value : '';
};

// Engineer override: `?t=horne` on the portal URL forces the Horne/BDO tenant
// authority at the BFF (/api/v1/auth/login?...&t=horne). Without forwarding it,
// the Sign-in button drops the param and login goes to the default tenant.
const readTHorne = () => {
  if (typeof window === 'undefined' || !window.location) return false;
  return new URLSearchParams(window.location.search).get('t') === 'horne';
};

// Resolve where an already-authenticated revisit of /:vanity should land.
// Vanity / custom-domain envs (REACT_APP_BASE_DOMAIN set): the tenant lives at
// https://<vanity>.<base>. DNS-free mode (REACT_APP_BASE_DOMAIN empty): there is
// no <vanity>.<base> host — building one yields a dead `https://<vanity>.` URL —
// so stay on the real SPA host the user is already on (window.location.origin).
const resolveVanityLandingUrl = (vanity) => {
  const base = (process.env.REACT_APP_BASE_DOMAIN || '').trim().replace(/\.+$/, '');
  if (base) {
    return `https://${vanity}.${base}`;
  }
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return null;
};

const BrandedLoginPage = () => {
  const { vanity } = useParams();
  const requestedVanity = normalizeVanity(vanity);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const loginHint = readLoginHint();
  const tHorne = readTHorne();

  useEffect(() => {
    if (
      isSessionCookieTrue()
      && requestedVanity
      && getVanityFromCookie() === requestedVanity
    ) {
      const target = resolveVanityLandingUrl(requestedVanity);
      if (target) {
        window.location.assign(target);
      }
    }
  }, [requestedVanity]);

  useEffect(() => {
    if (!requestedVanity) {
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const fetchBranding = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/v1/tenant/info/${encodeURIComponent(requestedVanity)}`,
          { signal: controller.signal },
        );
        if (response.ok) {
          setBranding(await response.json());
        }
      } catch (err) {
        // on error, branding stays null and LoginPage uses its defaults
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchBranding();
    return () => controller.abort();
  }, [requestedVanity]);

  useEffect(() => { setFavicon(branding?.faviconUrl); }, [branding?.faviconUrl]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center',
      }}
      >
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          border: '5px solid rgba(17, 68, 155, 0.2)',
          borderTopColor: '#11449b',
          animation: 'spin 0.7s linear infinite',
        }}
        />
      </div>
    );
  }

  return (
    <LoginPage
      requestedVanity={requestedVanity}
      branding={branding}
      loginHint={loginHint}
      tHorne={tHorne}
    />
  );
};

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<LoginPage />} />
    <Route path="/:vanity" element={<BrandedLoginPage />} />
    <Route path="*" element={<NotFoundPage />} />
  </Routes>
);

export default AppRoutes;
